export interface YearMonthDay {
  year: string
  month: string
  day: string
}

/**
 * Return today's date as { year, month, day } with month and day zero-padded to 2 digits.
 */
export function getTodayYMD(): YearMonthDay {
  const now = new Date()
  const year = now.getFullYear().toString()
  const month = (now.getMonth() + 1).toString().padStart(2, '0')
  const day = now.getDate().toString().padStart(2, '0')
  return { year, month, day }
}

/**
 * Parse YYYY-MM-DD into { year, month, day } with month/day zero-padded.
 * Returns getTodayYMD() if the string is empty or invalid.
 */
export function parseDateStringToYMD(dateString: string): YearMonthDay {
  if (!dateString || typeof dateString !== 'string') {
    return getTodayYMD()
  }
  const trimmed = dateString.trim()
  if (trimmed.length === 0) {
    return getTodayYMD()
  }
  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed)
  if (!match) {
    return getTodayYMD()
  }
  const [, year, month, day] = match
  const monthNum = parseInt(month, 10)
  const dayNum = parseInt(day, 10)
  if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
    return getTodayYMD()
  }
  return {
    year,
    month: month.padStart(2, '0'),
    day: day.padStart(2, '0')
  }
}

/**
 * Build YYYY-MM-DD from year, month, day strings.
 * Pads month and day to 2 digits. Returns '' if any part is missing or invalid.
 */
export function buildDateStringFromYMD(year: string, month: string, day: string): string {
  const y = year.trim()
  const m = month.trim().padStart(2, '0')
  const d = day.trim().padStart(2, '0')
  if (y.length !== 4 || m.length !== 2 || d.length !== 2) {
    return ''
  }
  const monthNum = parseInt(m, 10)
  const dayNum = parseInt(d, 10)
  if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
    return ''
  }
  return `${y}-${m}-${d}`
}
